import { Buffer } from 'buffer';
import * as Yup from 'yup';
import { CERTIFICATE_PLACEHOLDER } from './integrations.utils';

// Common regex patterns
const HOST_REGEX =
	/^https?:\/\/(?:(?:[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+)|(?:(?:[0-9]{1,3}\.){3}[0-9]{1,3})|(?:\[[0-9a-fA-F:]+\]))(?::[0-9]{1,5})?(?:\/[A-Za-z0-9\-_]+)?$/;
const NO_WHITESPACE_REGEX = /^\S*$/;
const DOMAIN_REGEX =
	/^(?:[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+|(?:(?:[0-9]{1,3}\.){3}[0-9]{1,3})|\[[0-9a-fA-F:]+\])$/;
const CLIENT_ID_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9\-_]{2,63}$/;

// Custom error messages
const ERROR_MESSAGES = {
	NO_WHITESPACE: 'Must not contain whitespace or newlines',
	INVALID_HOST: 'Must be a valid host URL (e.g. https://example.com)',
	INVALID_PORT: 'Port must be a number between 1 and 9999999',
	INVALID_EMAIL: 'Must be a valid email address',
	INVALID_DOMAIN: 'Must be a valid domain name (e.g. example.com)',
	INVALID_CLIENT_ID:
		'Must be 3-64 characters long and contain only letters, numbers, hyphens, and underscores',
} as const;

// Reusable validators
export const noWhitespaceValidator = Yup.string().matches(
	NO_WHITESPACE_REGEX,
	ERROR_MESSAGES.NO_WHITESPACE
);

export const hostValidator = Yup.string().matches(
	HOST_REGEX,
	ERROR_MESSAGES.INVALID_HOST
);

export const portValidator = Yup.number().integer().min(1).max(9999999);

export const emailValidator = Yup.string().email(ERROR_MESSAGES.INVALID_EMAIL);

export const domainValidator = Yup.string().matches(
	DOMAIN_REGEX,
	ERROR_MESSAGES.INVALID_DOMAIN
);

export const clientIdValidator = Yup.string().matches(
	CLIENT_ID_REGEX,
	ERROR_MESSAGES.INVALID_CLIENT_ID
);

// Schema extensions
export const passwordSchema = Yup.string().concat(noWhitespaceValidator).meta({
	title: 'Password',
	label: 'Password',
	htmlType: 'password',
});

export const apiKeySchema = Yup.string().concat(noWhitespaceValidator).meta({
	label: 'API Key',
	title: 'API Key',
	format: 'password',
	htmlType: 'password',
});

export const hostSchema = Yup.string().concat(hostValidator).meta({
	title: 'Host',
	label: 'Host',
	htmlType: 'text',
});

export const portSchema = Yup.number().concat(portValidator).meta({
	title: 'Port',
	label: 'Port',
	htmlType: 'number',
});

export const emailSchema = Yup.string().concat(emailValidator).meta({
	title: 'Email',
	label: 'Email',
	htmlType: 'email',
});

export const domainSchema = Yup.string().concat(domainValidator).meta({
	title: 'Domain',
	label: 'Domain',
	htmlType: 'text',
});

export const clientIdSchema = Yup.string().concat(clientIdValidator).meta({
	title: 'Client ID',
	label: 'Client ID',
	htmlType: 'text',
});

export const pemSchema = Yup.string()
	.test('is-valid-pem', 'Must be a valid PEM encoding', (value) => {
		// If the value is empty, we consider it valid
		if (!value) return true;
		try {
			const decoded = Buffer.from(value, 'base64').toString('utf-8').trim();
			const result = isValidPem(decoded);
			return typeof result === 'boolean' ? result : result.length > 0;
		} catch {
			return false;
		}
	})
	.meta({
		placeholder: CERTIFICATE_PLACEHOLDER,
		htmlType: 'textarea',
	});

/**
 * Represents a parsed PEM block.
 */
interface PemBlock {
	label: string;
	header: string;
	footer: string;
	base64Content: string;
	derContent?: Buffer; // Optional: decoded DER content
}

/**
 * Validates if a string contains correctly encoded PEM formatted data.
 *
 * A PEM string should have:
 * 1. A header line: `-----BEGIN <label>-----`
 * 2. Base64 encoded data.
 * 3. A footer line: `-----END <label>-----`
 * The label in the header and footer must match.
 * The string can contain multiple concatenated PEM blocks.
 *
 * @param pemString The string to validate.
 * @returns True if the string is valid PEM format, false otherwise.
 * Returns an array of PemBlock objects if valid and `extractBlocks` is true.
 */
export function isValidPem(pemString: string): boolean | PemBlock[] {
	if (typeof pemString !== 'string' || pemString.trim() === '') {
		return false;
	}

	const cleanedString = pemString.trim();
	const pemBlocks: PemBlock[] = [];

	// Regex to find potential PEM blocks. It captures the label and the content.
	// It looks for -----BEGIN LABEL----- followed by base64-like characters (allowing newlines)
	// and then -----END LABEL-----. The \1 ensures the end label matches the begin label.
	const pemRegex =
		/-----BEGIN ([A-Z0-9\s]+)-----\s*([\s\S]+?)\s*-----END \1-----/g;

	let match;
	let lastEndPosition = 0;

	while ((match = pemRegex.exec(cleanedString)) !== null) {
		const label = match[1].trim();
		const fullBlock = match[0];
		const contentWithNewlines = match[2];

		// Check if blocks are contiguous or if there's non-PEM data between them
		// that is not just whitespace. RFC 7468 allows for "explanatory text"
		// outside of the PEM block delimiters. For a strict validator of "only PEM data",
		// you might want to ensure `match.index` is very close to `lastEndPosition`
		// (allowing for whitespace).
		const textBetween = cleanedString.substring(lastEndPosition, match.index);
		if (textBetween.trim().length > 0 && !/^\s*$/.test(textBetween)) {
			// Found significant non-whitespace text between valid PEM blocks.
			// Depending on strictness, this could be an error.
			// For this validator, we'll allow it as per RFC 7468's "explanatory text".
		}

		if (!label) {
			return false; // Label cannot be empty
		}

		// Remove newlines and whitespace from the content for Base64 validation
		const base64Content = contentWithNewlines.replace(/\s+/g, '');

		if (base64Content.length === 0) {
			// Content can be empty for some PEM types, but it's unusual for keys/certs.
			// Let's consider it valid structurally, actual parsing would fail later.
		}

		// Validate Base64 content
		// A more robust check would be to try decoding it.
		// This regex checks for valid Base64 characters and optional padding.
		const base64Regex = /^[A-Za-z0-9+/]*=?=?$/;
		if (!base64Regex.test(base64Content)) {
			return false; // Content is not valid Base64
		}

		// Try to decode Base64 to ensure it's valid
		let derContent: Buffer | undefined = undefined;
		try {
			derContent = Buffer.from(base64Content, 'base64');
			// The Buffer.from will not throw an error for non-canonical Base64,
			// but it will produce a result. If the original base64Content was
			// truly invalid (e.g. invalid characters not caught by regex),
			// it might produce unexpected Buffer content.
			// A stricter validation might re-encode and compare, or use a library.
			if (
				Buffer.from(derContent.toString('base64'), 'base64').toString(
					'base64'
				) !== base64Content &&
				base64Content.length > 0
			) {
				// This check is a bit more involved. If re-encoding doesn't match,
				// it might imply non-canonical base64 or an issue.
				// However, some base64 encoders/decoders handle padding differently.
				// For simplicity, if it decodes without throwing for invalid characters,
				// we'll accept it at this stage.
			}
		} catch (e) {
			return false; // Base64 decoding failed
		}

		pemBlocks.push({
			label: label,
			header: `-----BEGIN ${label}-----`,
			footer: `-----END ${label}-----`,
			base64Content: contentWithNewlines.trim(), // Keep original newlines for content
			derContent: derContent,
		});

		lastEndPosition = pemRegex.lastIndex;
	}

	// After finding all blocks, check if the entire string (minus allowed explanatory text)
	// was consumed by these blocks.
	// If lastEndPosition doesn't reach near the end of the string, it means
	// there's trailing data that isn't part of a valid PEM block and isn't just whitespace.
	const remainingText = cleanedString.substring(lastEndPosition).trim();
	if (remainingText.length > 0 && !/^\s*$/.test(remainingText)) {
		// Found trailing non-whitespace text that is not part of a valid PEM block.
		// Could be an error depending on strictness.
		// If we allow explanatory text anywhere, this might be acceptable.
		// However, if `pemBlocks` is empty and there was text, it's definitely not PEM.
		if (pemBlocks.length === 0) return false;
	}

	if (pemBlocks.length === 0 && cleanedString.length > 0) {
		return false; // No PEM blocks found in a non-empty string
	}

	return pemBlocks.length > 0;
}
